import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalBody,
   ModalFooter,
   ModalCloseButton,
   Box,
   Grid,
   VStack,
   Flex,
   Button,
   Text,
   Heading,
   Avatar,
   PinInput,
   PinInputField,
   useColorModeValue,
   Badge,
   ScaleFade,
   SlideFade,
   Progress,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getUsers, spVerify } from '../../../../../redux/actions/user';
import { sendVerifyOtp } from '../../../../../redux/actions/account';
// import { verifyOtp } from '../../../../../redux/actions/account';

// Icons using Unicode symbols
const AlertIcon = () => <Text fontSize="2xl">⚠️</Text>;
const CheckIcon = () => <Text fontSize="xl">✓</Text>;

const PriceVerificationModal = ({ isOpen, onClose, onSave }) => {
   const dispatch = useDispatch();
   const skipBtnRef = useRef();

   const [userData, setUserData] = useState([]);
   const [selectedUser, setSelectedUser] = useState(null);
   const [otpSent, setOtpSent] = useState(false);
   const [otp, setOtp] = useState('');
   const [resendTimer, setResendTimer] = useState(0);
   const [loading, setLoading] = useState(false);

   // Color mode values
   const bgColor = useColorModeValue('white', 'gray.800');
   const borderColor = useColorModeValue('gray.200', 'gray.600');
   const hoverBg = useColorModeValue('gray.50', 'gray.700');
   const selectedBg = useColorModeValue('blue.50', 'blue.900');
   const alertBg = useColorModeValue('orange.50', 'orange.900');
   const textSecondary = useColorModeValue('gray.600', 'gray.400');

   /* Fetch users when modal opens */
   useEffect(() => {
      if (!isOpen) return;

      const fetchUsers = async () => {
         const res = await dispatch(getUsers({ getSpOtp: true }));
         setUserData(res?.data || []);
      };

      fetchUsers();
   }, [isOpen, dispatch]);

   /* Resend OTP countdown */
   useEffect(() => {
      if (!resendTimer) return;

      const timer = setInterval(() => {
         setResendTimer((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
   }, [resendTimer]);

   const handleSendOtp = async () => {
      if (!selectedUser) return;

      setLoading(true);
      const res = await dispatch(spVerify({ userId: selectedUser.id }));
      console.log("send otp", res);
      setOtpSent(true);
      setResendTimer(30);
      setLoading(false);
   };

   const handleVerifyOtp = async () => {
      setLoading(true);
      const res = await dispatch(spVerify({ userId: selectedUser.id, otp: Number(otp) }));
      console.log("verify otp", res);
      setLoading(false);
      if (res && res.code && res.code === 1) {
         onSave();
         resetState();
         onClose();
      }
   };

   const resetState = () => {
      setSelectedUser(null);
      setOtp('');
      setOtpSent(false);
      setResendTimer(0);
   };

   return (
      <Modal
         isOpen={isOpen}
         onClose={() => {
            resetState();
            onClose();
         }}
         size="3xl"
         isCentered
         finalFocusRef={skipBtnRef}
         motionPreset="slideInBottom"
      >
         <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
         <ModalContent
            bg={bgColor}
            borderRadius="2xl"
            boxShadow="2xl"
            overflow="hidden"
            maxH="90vh"
         >
            {/* Header with gradient accent */}
            <Box
               bgGradient="linear(to-r, orange.400, red.400)"
               h="4px"
               w="full"
            />

            <ModalHeader pt={6} pb={4} px={8}>
               <Flex gap={3} mb={2} align="flex-start">
                  <Box
                     bg={alertBg}
                     p={2}
                     borderRadius="lg"
                     display="flex"
                     alignItems="center"
                     justifyContent="center"
                  >
                     <AlertIcon />
                  </Box>
                  <Box flex={1}>
                     <Heading size="lg" mb={1}>
                        Price Verification Required
                     </Heading>
                     <Text fontSize="sm" color={textSecondary} fontWeight="normal">
                        Sales price is lower than purchase price. Authorization needed to proceed.
                     </Text>
                  </Box>
               </Flex>
            </ModalHeader>
            <ModalCloseButton top={6} right={6} />

            <ModalBody px={8} py={6}>
               <VStack spacing={6} align="stretch">
                  {/* USER SELECTION - Only show when OTP not sent */}
                  {!otpSent && (
                     <ScaleFade initialScale={0.95} in={!otpSent}>
                        <VStack spacing={4} align="stretch">
                           {/* Selected User Display */}
                           {selectedUser && (
                              <Box
                                 p={4}
                                 bg={selectedBg}
                                 borderRadius="xl"
                                 borderWidth="2px"
                                 borderColor="blue.400"
                              >
                                 <Flex justify="space-between" align="center">
                                    <Flex gap={3} align="center">
                                       <Avatar
                                          src={selectedUser.image}
                                          name={selectedUser.name}
                                          size="md"
                                       />
                                       <VStack align="start" spacing={0}>
                                          <Text fontWeight="bold" fontSize="md">
                                             {selectedUser.name}
                                          </Text>
                                          <Text fontSize="sm" color={textSecondary}>
                                             {selectedUser.mobile}
                                          </Text>
                                       </VStack>
                                    </Flex>
                                    <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
                                       Selected
                                    </Badge>
                                 </Flex>
                              </Box>
                           )}

                           <Box>
                              <Text fontWeight="semibold" mb={3} fontSize="sm" color={textSecondary}>
                                 Available Verifiers ({userData.length})
                              </Text>
                              <Grid
                                 templateColumns="repeat(auto-fill, minmax(200px, 1fr))"
                                 gap={3}
                                 maxH="300px"
                                 overflowY="auto"
                                 pr={2}
                                 css={{
                                    '&::-webkit-scrollbar': {
                                       width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                       background: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                       background: '#CBD5E0',
                                       borderRadius: '4px',
                                    },
                                 }}
                              >
                                 {userData?.map((user) => (
                                    <Box
                                       key={user.id}
                                       p={3}
                                       borderWidth="2px"
                                       borderRadius="xl"
                                       cursor="pointer"
                                       borderColor={
                                          selectedUser?.id === user.id
                                             ? 'blue.400'
                                             : borderColor
                                       }
                                       bg={
                                          selectedUser?.id === user.id
                                             ? selectedBg
                                             : 'transparent'
                                       }
                                       _hover={{
                                          bg: selectedUser?.id === user.id ? selectedBg : hoverBg,
                                          transform: 'translateY(-2px)',
                                          shadow: 'md',
                                       }}
                                       transition="all 0.2s"
                                       onClick={() => setSelectedUser(user)}
                                    >
                                       <VStack spacing={2} align="center">
                                          <Avatar
                                             src={user.image}
                                             name={user.name}
                                             size="md"
                                          />
                                          <VStack spacing={0} align="center">
                                             <Text fontWeight="600" fontSize="sm" textAlign="center">
                                                {user.name}
                                             </Text>
                                             <Text fontSize="xs" color={textSecondary}>
                                                {user.mobile}
                                             </Text>
                                          </VStack>
                                       </VStack>
                                    </Box>
                                 ))}
                              </Grid>
                           </Box>
                        </VStack>
                     </ScaleFade>
                  )}

                  {/* OTP SECTION - Only show when OTP sent */}
                  {otpSent && (
                     <SlideFade in={otpSent} offsetY="20px">
                        <VStack spacing={5} py={4}>
                           {/* Selected User in OTP View */}
                           <Box
                              p={4}
                              bg={selectedBg}
                              borderRadius="xl"
                              w="full"
                              borderWidth="1px"
                              borderColor="blue.300"
                           >
                              <Flex gap={3} align="center">
                                 <Avatar
                                    src={selectedUser?.image}
                                    name={selectedUser?.name}
                                    size="sm"
                                 />
                                 <VStack align="start" spacing={0} flex={1}>
                                    <Text fontWeight="bold" fontSize="sm">
                                       Verifying with {selectedUser?.name}
                                    </Text>
                                    <Text fontSize="xs" color={textSecondary}>
                                       OTP sent to {selectedUser?.mobile}
                                    </Text>
                                 </VStack>
                              </Flex>
                           </Box>

                           <VStack spacing={3}>
                              <Text fontWeight="semibold" fontSize="sm" color={textSecondary}>
                                 Enter 6-digit OTP
                              </Text>
                              <Flex gap={3} justify="center">
                                 <PinInput
                                    otp
                                    size="lg"
                                    onChange={setOtp}
                                    placeholder="○"
                                 >
                                    <PinInputField
                                       borderWidth="2px"
                                       borderRadius="lg"
                                       _focus={{
                                          borderColor: 'blue.400',
                                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                                       }}
                                    />
                                    <PinInputField
                                       borderWidth="2px"
                                       borderRadius="lg"
                                       _focus={{
                                          borderColor: 'blue.400',
                                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                                       }}
                                    />
                                    <PinInputField
                                       borderWidth="2px"
                                       borderRadius="lg"
                                       _focus={{
                                          borderColor: 'blue.400',
                                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                                       }}
                                    />
                                    <PinInputField
                                       borderWidth="2px"
                                       borderRadius="lg"
                                       _focus={{
                                          borderColor: 'blue.400',
                                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                                       }}
                                    />
                                    <PinInputField
                                       borderWidth="2px"
                                       borderRadius="lg"
                                       _focus={{
                                          borderColor: 'blue.400',
                                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                                       }}
                                    />
                                    <PinInputField
                                       borderWidth="2px"
                                       borderRadius="lg"
                                       _focus={{
                                          borderColor: 'blue.400',
                                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                                       }}
                                    />
                                 </PinInput>
                              </Flex>
                           </VStack>

                           {resendTimer > 0 && (
                              <Box w="full">
                                 <Progress
                                    value={(resendTimer / 30) * 100}
                                    size="xs"
                                    colorScheme="blue"
                                    borderRadius="full"
                                 />
                              </Box>
                           )}

                           <Flex justify="space-between" w="full" pt={2}>
                              <Button
                                 variant="ghost"
                                 size="sm"
                                 isDisabled={resendTimer > 0}
                                 onClick={handleSendOtp}
                                 colorScheme="blue"
                              >
                                 {resendTimer > 0
                                    ? `Resend in ${resendTimer}s`
                                    : 'Resend OTP'}
                              </Button>

                              <Button
                                 colorScheme="green"
                                 size="md"
                                 isDisabled={otp.length !== 6}
                                 isLoading={loading}
                                 onClick={handleVerifyOtp}
                                 px={8}
                                 borderRadius="lg"
                                 leftIcon={<CheckIcon />}
                              >
                                 Verify & Proceed
                              </Button>
                           </Flex>
                        </VStack>
                     </SlideFade>
                  )}
               </VStack>
            </ModalBody>

            <ModalFooter px={8} py={6} bg={useColorModeValue('gray.50', 'gray.900')}>
               <Flex gap={3} w="full" justify="flex-end">
                  {!otpSent && (
                     <Button
                        colorScheme="blue"
                        isDisabled={!selectedUser}
                        isLoading={loading}
                        onClick={handleSendOtp}
                        size="lg"
                        px={8}
                        borderRadius="lg"
                     >
                        Send OTP
                     </Button>
                  )}

                  <Button
                     variant="ghost"
                     onClick={() => {
                        resetState();
                        onClose();
                     }}
                     size="lg"
                     px={6}
                     borderRadius="lg"
                  >
                     Cancel
                  </Button>
               </Flex>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default PriceVerificationModal;